// src/hooks/useQuestionnaireDimensionRiskRules.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  createQuestionnaireDimensionRiskRule,
  deleteQuestionnaireDimensionRiskRule,
  getQuestionnaireDimensionRiskRule,
  listQuestionnaireDimensionRiskRuleLookups,
  listQuestionnaireDimensionRiskRules,
  setQuestionnaireDimensionRiskRuleStatus,
  updateQuestionnaireDimensionRiskRule,
} from "@/services/questionnaireDimensionRiskRules";
import type { GroCrudScope } from "@/lib/groCrudScope";
import type { QuestionnaireDimensionRiskRuleUpsertIn } from "@/types/questionnaireDimensionRiskRules";

export function useQuestionnaireDimensionRiskRules(
  params: { dimension_id?: string; is_active?: boolean },
  scope: GroCrudScope = "client",
) {
  return useQuery({
    queryKey: ["questionnaire_dimension_risk_rules", scope, params],
    queryFn: () => listQuestionnaireDimensionRiskRules(params, scope),
    staleTime: 20_000,
  });
}

export function useQuestionnaireDimensionRiskRuleDetail(
  ruleId?: string,
  scope: GroCrudScope = "client",
) {
  return useQuery({
    queryKey: ["questionnaire_dimension_risk_rule_detail", scope, ruleId],
    queryFn: () => getQuestionnaireDimensionRiskRule(ruleId as string, scope),
    enabled: !!ruleId,
    staleTime: 20_000,
  });
}

export function useQuestionnaireDimensionRiskRuleLookups(scope: GroCrudScope = "client") {
  return useQuery({
    queryKey: ["questionnaire_dimension_risk_rule_lookups", scope],
    queryFn: () => listQuestionnaireDimensionRiskRuleLookups(scope),
    staleTime: 60_000,
  });
}

export function useCreateQuestionnaireDimensionRiskRule(scope: GroCrudScope = "client") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: QuestionnaireDimensionRiskRuleUpsertIn) =>
      createQuestionnaireDimensionRiskRule(body, scope),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["questionnaire_dimension_risk_rules", scope] });
      qc.invalidateQueries({ queryKey: ["questionnaire_dimension_risk_rule_lookups", scope] });
      qc.invalidateQueries({ queryKey: ["company_gro_templates"] });
    },
  });
}

export function useUpdateQuestionnaireDimensionRiskRule(scope: GroCrudScope = "client") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ruleId, body }: { ruleId: string; body: QuestionnaireDimensionRiskRuleUpsertIn }) =>
      updateQuestionnaireDimensionRiskRule(ruleId, body, scope),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["questionnaire_dimension_risk_rules", scope] });
      qc.invalidateQueries({
        queryKey: ["questionnaire_dimension_risk_rule_detail", scope, variables.ruleId],
      });
      qc.invalidateQueries({ queryKey: ["company_gro_templates"] });
    },
  });
}

export function useSetQuestionnaireDimensionRiskRuleStatus(scope: GroCrudScope = "client") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ruleId, is_active }: { ruleId: string; is_active: boolean }) =>
      setQuestionnaireDimensionRiskRuleStatus(ruleId, is_active, scope),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["questionnaire_dimension_risk_rules", scope] });
      qc.invalidateQueries({
        queryKey: ["questionnaire_dimension_risk_rule_detail", scope, variables.ruleId],
      });
      qc.invalidateQueries({ queryKey: ["company_gro_templates"] });
    },
  });
}

export function useDeleteQuestionnaireDimensionRiskRule(scope: GroCrudScope = "client") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ruleId: string) => deleteQuestionnaireDimensionRiskRule(ruleId, scope),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["questionnaire_dimension_risk_rules", scope] });
      qc.invalidateQueries({ queryKey: ["company_gro_templates"] });
    },
  });
}
